// @flow
import { combineReducers } from 'redux';
import type { BaseAction as Action, State } from './types';
import app from './app/reducer';
import auth from './auth/reducer';
import carousels from './carousels/reducer';
import config from './config/reducer';
import device from './device/reducer';
import dvr from './dvr/reducer';
import est from './est/reducer';
import feedback from './feedback/reducer';
import franchise from './franchise/reducer';
import guide from './guide/reducer';
import home from './home/reducer';
import intl from './intl/reducer';
import network from './network/reducer';
import networkInfo from './networkInfo/reducer';
import networks from './networks/reducer';
import onDemand from './onDemand/reducer';
import player from './player/reducer';
import preferences from './preferences/reducer';
import profiles from './profiles/reducer';
import profilesSecurity from './profiles_security/reducer';
import search from './search/reducer';
import sports from './sports/reducer';
import team from './team/reducer';
import users from './users/reducer';
import watchlist from './watchlist/reducer';
import websocket from './websocket/reducer';
import cast from './cast/reducer';
import sportsBracket from './sportsBrackets/reducer';
import analytics from './analytics/reducer';

// stackoverflow.com/q/35622588/233902
const resetStateOnSignOutReducer = (reducer, initialState) => (state: State, action: Action) => {
  const userWasSignedOut =
    // $FlowFixMe
    action.type === 'ON_AUTH' && state.auth.viewer && !action.payload.firebaseUser;
  if (!userWasSignedOut) {
    return reducer(state, action);
  }
  // Note how we can purge sensitive data without hard reload easily.
  let stateWithoutSensitiveData = {
    app: state.app,
    config: initialState.config,
    device: initialState.device,
    intl: initialState.intl,
  };
  // Preserve Found router reducer.
  if (process.env.IS_BROWSER) {
    stateWithoutSensitiveData = {
      ...stateWithoutSensitiveData,
      found: state.found,
    };
  }
  return reducer(stateWithoutSensitiveData, action);
};

const spreadSetRecursive = (parent, path, value) => {
  const key = path.shift();
  if (path.length === 0) {
    // $FlowFixMe
    parent[key] = value;
  } else {
    // $FlowFixMe
    parent[key] = { ...parent[key] };
    // $FlowFixMe
    spreadSetRecursive(parent[key], path, value);
  }
};

const applyFirebaseConfigHor = reducer => (state: State, action: Action) => {
  if (action.type === 'SET_FIREBASE_CONFIG') {
    // Apply firebase configuration at appropriate store places
    const { config, level } = action;
    Object.keys(config).forEach(key => {
      const path = key.split('.').filter(item => !!item);
      const debugPath = ['firebase', level, ...path];
      const value = config[key];
      spreadSetRecursive(state.config, path, value);
      if (!debugPath[debugPath.length - 1].includes('config_')) {
        spreadSetRecursive(state.config, debugPath, value);
      }
    });
  }
  return reducer(state, action);
};

/* Adds support for 'CAST_SET_STORE' action in Chromecast mode. 
   It affects (completely rewrites) global state at root level 
   (so the wrapper executes before main reducer). */
const applyChromecastReceiverHor = reducer => (state: State, action: Action): State =>
  action.type === 'CAST_SET_STORE' ? { ...state, ...action.payload } : reducer(state, action);

const configureReducer = (platformReducers: Object, initialState: Object) => {
  let reducer = combineReducers({
    ...platformReducers,
    app,
    auth,
    carousels,
    config,
    cast,
    device,
    dvr,
    est,
    feedback,
    franchise,
    guide,
    home,
    intl,
    network,
    networkInfo,
    networks,
    onDemand,
    player,
    preferences,
    profiles,
    profilesSecurity,
    search,
    sports,
    sportsBracket,
    team,
    users,
    watchlist,
    websocket,
    analytics,
  });

  // The power of higher-order reducers, http://slides.com/omnidan/hor
  reducer = resetStateOnSignOutReducer(reducer, initialState);
  reducer = applyFirebaseConfigHor(reducer);
  reducer = process.env.IN_CHROMECAST_MODE ? applyChromecastReceiverHor(reducer) : reducer;

  return reducer;
};

export default configureReducer;
